import { RootState } from '@next/core-lib/app';
import { ActionItem } from '@next/core-lib/components';

import { CropAidState, UnitOfMeasurementEnum } from '../crop-aid/crop-aid.model';
import { SoilTypeState } from '../soil-type/soil-type.model';

export interface SiteState extends RootState {
  appState: AppState;
  templateState: TemplateState;
  cropAidState: CropAidState;
  soilTypeState: SoilTypeState;
}

export interface AppState {
  crops?: Crop[];
  templates?: Template[];
  cropYear?: number;
  defaultCropYear?: number;
  availableCropYears?: number[];
  salesOrderPeriods?: SalesOrderPeriod[];
  templatePagination?: PaginationState;
  templateFilter: TemplateFilter;
  nutrientProperties?: NutrientProperties[];
  nutrientSources?: NutrientSource[];
  nutrientDesignations?: NutrientDesignation[];
  soilTypes?: SoilType[];
  unitsOfMeasure?: UnitOfMeasure[];
  varietyProperties?: VarietyProperties[];
  cropVarieties?: CropVariety[];
  productionPurposeCategories?: ProductionPurposeCategory[];
  productionPurposeCategoriesDetailed?: ProductionPurposeCategoryDetailed[];
  nitrogenModelProperties?: NitrogenModelProperties[];
}

export interface TemplateState {
  editingTemplate?: Template;
}

export enum TemplateType {
  GBM = 'GBM',
  Fertilization = 'Fertilization',
}

export enum TreatmentType {
  GBM = 'GBM',
  Fertilization = 'Fertilization',
}
export interface Template {
  id: number;
  cropYear: number;
  cropId: number;
  soilTypeId: number;
  isSkal: boolean;
  isPlanetProof: boolean;
  isGroundWaterPreservation: boolean;
  treatments: Treatment[];
  treatmentSchemas: TreatmentSchema[];
  templateType: TemplateType;
  isCorrupt?: boolean;
  defaultTreatmentSchemaId: string | null;
  province: ProvinceEnum[];
}

export interface TreatmentSchema {
  id: string;
  name: string;
  description: string;
  treatmentIds: string[];
  isCorrupt?: boolean;
}

export interface ModifyingEmployee {
  id: number;
  firstName: string;
  lastName: string;
}

export interface Treatment {
  id: string;
  aidAdvices: AidAdvice[];
  moment: string;
  name: string;
  objective: string;
  periodId: number;
  pointOfAttention: string;
  frequency: number;
  driftReduction?: number;
  treatmentType: TreatmentType;
  modifiedByEmployee?: ModifyingEmployee;
  dateModified?: string;
  isCorrupt?: boolean;
  forbiddenForPlanetProof?: boolean;
}

export interface TreatmentWithActions extends Treatment {
  hasDate: boolean;
  actions: ActionItem[];
}

export interface AidAdvice {
  cropAidId: number;
  dosage: number;
  forbiddenForPlanetProof?: boolean;
  unitOfMeasurement?: UnitOfMeasurementEnum;
}

export interface SalesOrderPeriod {
  id: number;
  year: number;
  periodNumber: number;
  name: string;
  periodType?: TemplateType;
}

export interface Crop {
  id: number;
  name: string;
}

export interface GetCropsApiResponse {
  id: number;
  agrifirmCrop: {
    name: string;
  };
}

export interface TemplateTableItem extends Template {
  cropName: string;
  soilTypeName: string;
}

export interface CopyTreatmentsRequest {
  templateId: number;
  cropId: number;
  cropYears: number[];
  soilTypeIds: (number | null)[];
  treatmentIds: string[];
  schemaIds: string[];
  isSkalOptions: boolean[];
  isPlanetProofOptions: boolean[];
  isGroundWaterPreservationOptions: boolean[];
  templateType: TemplateType;
  province: ProvinceEnum[];
}

export interface CopyTreatmentsResponse {
  numberOfTemplatesAffected: number;
  reasonCode?: string | null;
  reason?: string | null;
  reasons?: string[] | null;
}

export interface UpdateDefaultTreatmentSchemaIdForTemplateRequest {
  treatmentSchemaId: string | null;
}

export interface CopyTemplatesByYearRequest {
  sourceYear: number;
  destinationYear: number;
}

export interface CopyTemplatesByYearResponse {
  numberOfTemplatesCopied: number;
}

export interface GetTemplatesResponse {
  templates: Template[];
  pagination: PaginationState;
}

export interface PaginationState {
  pageNumber: number;
  pageSize: number;
  totalNumberOfItems: number;
}

export interface TemplateFilter {
  [key: string]: number | string | boolean | ProvinceEnum | null;
  cropId: number | null;
  soilTypeId: number | null;
  isCorrupt: boolean | null;
  templateType: string | null;
  provinces: ProvinceEnum | null;
  cropAidId: number | null;
}

export interface Objective {
  name: string;
  value: string;
}

export interface CropAndCropAid {
  cropId: number;
  cropAidId: number;
}

export interface AutomaticAdviceCsvUploadResponse {
  failedRecords: string[];
}

export interface AutomaticAdviceCustomersPreferencesLastUpdateResponse {
  lastUpsertion: string;
}

export interface NutrientDesignation {
  designation: string;
}

export interface NutrientSource {
  source: string;
}

export enum NutrientPropertiesSampleNumber {
  One = 1,
  Two = 2,
  Three = 3,
  Independent = 9999,
}

export interface NutrientProperties {
  id: number;
  source: string;
  nutrientDesignation: string;
  soilTypeId: number;
  soilType: SoilType;
  sampleNumber: NutrientPropertiesSampleNumber;
  soilTargetUoM?: string;
  soilTargetLow?: number;
  soilTargetHigh?: number;
  plantTargetUoM?: string;
  plantTargetLow?: number;
  plantTargetHigh?: number;
}

export interface UpsertNutrientPropertiesRequest {
  id?: number;
  source: string;
  nutrientDesignation: string;
  soilTypeId: number;
  sampleNumber: NutrientPropertiesSampleNumber;
  soilTargetUoM?: string;
  soilTargetLow?: number;
  soilTargetHigh?: number;
  plantTargetUoM?: string;
  plantTargetLow?: number;
  plantTargetHigh?: number;
}

export interface SoilType {
  id: number;
  name: string;
}

export interface UnitOfMeasure {
  name: string;
}

export interface VarietyProperties {
  id: number;
  ediCode: number;
  cropVarietyName: string;
  productionPurposeCategoryId: number;
  productionPurposeCategoryName: string;
  foliageMax: number;
  day50Percent: number;
  nEfficiency: number;
  nMax: number;
  nNorm: number;
  kMin: number;
  kMax: number;
  giftcorrection: number;
  plantHa: number;
  maturity: number;
  mgMin: number;
  mgMax: number;
}

export interface UpsertVarietyPropertiesRequest {
  id?: number;
  ediCode: number;
  productionPurposeCategoryId: number;
  foliageMax: number;
  day50Percent: number;
  nEfficiency: number;
  nMax: number;
  nNorm: number;
  kMin: number;
  kMax: number;
  giftcorrection: number;
  plantHa: number;
  maturity: number;
  mgMin: number;
  mgMax: number;
}

export interface CropVariety {
  id: number;
  ediCode: number;
  name: string;
  cropId: number;
  ediCodeCrop: number;
  isDeprecated: boolean;
}

export interface ProductionPurposeCategory {
  id: number;
  name: string;
  description: string;
}
export interface ProductionPurposeCategoryDetailed {
  id: number;
  name: string;
  description: string;
  n1: number;
  n2: number;
  n3: number;
  baseYieldPotential: number;
  nCorrection: number;
  nO3NormTreshold: number;
  trendlineTuningA: number;
  trendlineTuningB: number;
  trendlineTuningC: number;
  trendlineTuningD: number;
}
export interface UpsertProductionPurposeCategoryRequest {
  id?: number;
  name: string;
  description: string;
  n1: number;
  n2: number;
  n3: number;
  baseYieldPotential: number;
  nCorrection: number;
  nO3NormTreshold: number;
  trendlineTuningA: number;
  trendlineTuningB: number;
  trendlineTuningC: number;
  trendlineTuningD: number;
}
export interface NitrogenModelProperties {
  id: number;
  nutrientDesignation: string;
  soilTypeId: number;
  soilType: SoilType;
  soilTargetUoM: string;
  nBasebuffer?: number;
  maxNbs?: number;
  maxNbsSeedPotato?: number;
}

export interface UpsertNitrogenModelPropertiesRequest {
  id?: number;
  nutrientDesignation: string;
  soilTypeId: number;
  soilTargetUoM: string;
  nBasebuffer?: number;
  maxNbs?: number;
  maxNbsSeedPotato?: number;
}

export enum SoilTargetUoMEnum {
  KgPerHa = 'kg/ha',
  MgPerL = 'mg/l',
}

export enum NutrientDesignationEnum {
  N = 'N',
  B = 'B',
  K = 'K',
  Mg = 'Mg',
  Mn = 'Mn',
  S = 'S',
}

export enum ProvinceEnum {
  Drenthe = 'Drenthe',
  Flevoland = 'Flevoland',
  Friesland = 'Friesland',
  Gelderland = 'Gelderland',
  Groningen = 'Groningen',
  Limburg = 'Limburg',
  NoordBrabant = 'NoordBrabant',
  NoordHolland = 'NoordHolland',
  Overijssel = 'Overijssel',
  Utrecht = 'Utrecht',
  Zeeland = 'Zeeland',
  ZuidHolland = 'ZuidHolland',
  Independent = 'Onafhankelijk',
}

export const provinces = [
  { label: 'Provincie Onafhankelijk', value: ProvinceEnum.Independent },
  { label: 'Drenthe', value: ProvinceEnum.Drenthe },
  { label: 'Flevoland', value: ProvinceEnum.Flevoland },
  { label: 'Friesland', value: ProvinceEnum.Friesland },
  { label: 'Gelderland', value: ProvinceEnum.Gelderland },
  { label: 'Groningen', value: ProvinceEnum.Groningen },
  { label: 'Limburg', value: ProvinceEnum.Limburg },
  { label: 'Noord-Brabant', value: ProvinceEnum.NoordBrabant },
  { label: 'Noord-Holland', value: ProvinceEnum.NoordHolland },
  { label: 'Overijssel', value: ProvinceEnum.Overijssel },
  { label: 'Utrecht', value: ProvinceEnum.Utrecht },
  { label: 'Zeeland', value: ProvinceEnum.Zeeland },
  { label: 'Zuid-Holland', value: ProvinceEnum.ZuidHolland },
];
